export interface LeadCustomerEntity {
  id: string
  company_name?: string | null
  contact_first_name?: string | null
  contact_last_name?: string | null
  email?: string | null
}

export type LeadCustomerSelection = { type: 'lead' | 'customer'; id: string } | null

export type LeadCustomerOption = {
  type: 'lead' | 'customer'
  id: string
  label: string
  email?: string | null
  searchText: string
}

export function formatLeadCustomerLabel(entity: LeadCustomerEntity): string {
  const company = entity.company_name?.trim()
  if (company) return company

  const name = `${entity.contact_first_name || ''} ${entity.contact_last_name || ''}`.trim()
  if (name) return name

  if (entity.email?.trim()) return entity.email.trim()

  return 'Unnamed'
}

function buildSearchText(entity: LeadCustomerEntity): string {
  return [
    entity.company_name,
    entity.contact_first_name,
    entity.contact_last_name,
    entity.email,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
}

export function buildLeadCustomerOptions(
  leads: LeadCustomerEntity[],
  customers: LeadCustomerEntity[]
): LeadCustomerOption[] {
  const leadOptions: LeadCustomerOption[] = leads.map((lead) => ({
    type: 'lead',
    id: lead.id,
    label: formatLeadCustomerLabel(lead),
    email: lead.email,
    searchText: buildSearchText(lead),
  }))

  const customerOptions: LeadCustomerOption[] = customers.map((customer) => ({
    type: 'customer',
    id: customer.id,
    label: formatLeadCustomerLabel(customer),
    email: customer.email,
    searchText: buildSearchText(customer),
  }))

  return [...leadOptions, ...customerOptions].sort((a, b) =>
    a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })
  )
}

export function filterLeadCustomerOptions(
  options: LeadCustomerOption[],
  query: string,
  maxResults = 50
): LeadCustomerOption[] {
  const trimmed = query.trim().toLowerCase()
  if (!trimmed) return options.slice(0, maxResults)

  return options
    .filter((option) => option.searchText.includes(trimmed) || option.label.toLowerCase().includes(trimmed))
    .slice(0, maxResults)
}

export function findLeadCustomerOption(
  options: LeadCustomerOption[],
  value: LeadCustomerSelection
): LeadCustomerOption | undefined {
  if (!value) return undefined
  return options.find((option) => option.type === value.type && option.id === value.id)
}

export function selectionFromIds(
  leadId?: string | null,
  customerId?: string | null
): LeadCustomerSelection {
  if (leadId) return { type: 'lead', id: leadId }
  if (customerId) return { type: 'customer', id: customerId }
  return null
}
